import { Injectable, inject } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Observable, combineLatest, debounceTime, switchMap, tap } from 'rxjs';
import { PageEvent } from 'shared/ui-common';

import {
  Pagination,
  GuidelineDto,
  OrderBy,
  PublicLinkDto,
  PublicLinkFilter,
  PublicLinkSort,
  PublicLinkSortBy,
} from '@treetop/shared/model';

import { PublicLinksHttpService } from './public-links.http.service';
interface PublicLinksState {
  readonly publicLinks: any[];
  readonly pagination: Pagination;
  readonly loading: boolean;
  readonly filter: PublicLinkFilter;
  readonly sorting: PublicLinkSort;
  readonly guideline: GuidelineDto;
}

@Injectable()
export class PublicLinksStore extends ComponentStore<PublicLinksState> {
  private http = inject(PublicLinksHttpService);

  public publicLinks$ = this.select((state) => state.publicLinks);
  public loading$ = this.select((state) => state.loading);
  public filters$ = this.select((state) => state.filter);
  public sorting$ = this.select((state) => state.sorting);
  public pagination$ = this.select((state) => state.pagination);
  public pageSize$ = this.select((state) => state.pagination.itemsPerPage);

  public readonly displayedPublicLinks$: Observable<any> = this.select(
    this.publicLinks$,
    this.loading$,
    this.pageSize$,

    (publicLinks, loading, pageSize) => {
      return loading ? this.generateLoadingMockPublicLinks(pageSize) : publicLinks;
    },
  );

  constructor() {
    super(initialState);

    this.getPublicLinks();
  }

  public readonly getPublicLinks = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      debounceTime(300),
      tap(() => this.patchState({ loading: true })),
      switchMap(() => {
        const { filter, pagination, sorting } = this.get();
        return this.http.get(filter, pagination, sorting).pipe(
          tapResponse(
            (response) => {
              this.patchState({
                publicLinks: response.list,
                loading: false,
                pagination: { ...pagination, maxElements: response.maxElements, maxPage: response.maxPage },
              });
            },
            () => this.patchState({ loading: false }),
          ),
        );
      }),
    );
  });

  public readonly getGuideline = this.effect((slug$: Observable<string>) => {
    return slug$.pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap((slug) =>
        this.http.getGuideline(slug).pipe(
          tapResponse(
            (guideline) => {
              this.patchState({
                guideline: guideline,
                loading: false,
              });
            },
            () => this.patchState({ loading: false }),
          ),
        ),
      ),
    );
  });

  public readonly addPublicLink = this.effect((link$: Observable<PublicLinkDto[]>) => {
    return link$.pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap((links) =>
        this.http.add(links).pipe(
          tapResponse(
            () => this.getPublicLinks(),
            () => this.patchState({ loading: false }),
          ),
        ),
      ),
    );
  });

  public readonly deletePublicLink = this.effect((publicLinkId$: Observable<string>) => {
    return publicLinkId$.pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap((id) =>
        this.http.delete(id).pipe(
          tapResponse(
            () => this.getPublicLinks(),
            () => this.patchState({ loading: false }),
          ),
        ),
      ),
    );
  });

  public readonly updatePagination = this.updater((state, pageEvent: PageEvent) => ({
    ...state,
    pagination: {
      page: pageEvent.page,
      itemsPerPage: pageEvent.itemsPerPage,
      maxPage: state.pagination.maxPage,
      maxElements: state.pagination.maxElements,
    },
  }));

  public readonly setSearchTerm = this.updater((state, searchTerm: string) => ({
    ...state,
    filter: {
      searchText: searchTerm,
    },
    pagination: {
      ...state.pagination,
      page: 1,
    },
  }));

  public readonly updateSorting = this.updater((state, sort: Sort) => ({
    ...state,
    sorting: {
      field: sort.active === 'createdAt' ? PublicLinkSortBy.CreatedAt : sort.active === 'createdBy' ? PublicLinkSortBy.CreatedBy : null,
      order: sort.direction === 'asc' ? OrderBy.Ascending : sort.direction === 'desc' ? OrderBy.Descending : null,
    },
  }));

  private generateLoadingMockPublicLinks(pageSize: number): any[] {
    const mockPublicLinks: any[] = [];
    for (let i = 0; i < pageSize; i++) {
      mockPublicLinks.push({
        id: i.toString(),
      });
    }
    return mockPublicLinks;
  }

  private readonly loadPublicLinksOnPaginationChange = this.effect(() =>
    combineLatest([this.select((state) => state.pagination.page), this.select((state) => state.pagination.itemsPerPage)]).pipe(
      tap(() => this.getPublicLinks()),
    ),
  );

  private readonly loadPublicLinksOnFilterChange = this.effect(() =>
    combineLatest([this.sorting$, this.filters$]).pipe(tap(() => this.getPublicLinks())),
  );
}

const initialState = {
  publicLinks: [],
  pagination: {
    page: 1,
    itemsPerPage: 10,
  },
  loading: false,
  sorting: {
    order: null,
    field: null,
  },
  filter: {
    createdById: null,
    isUsableOnly: false,
  },
  guideline: null,
};
